import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { getAllOrganizationsAsync, getAllUsersAsync } from '@/data/store/actions';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getCurrentSessionUser, getResourceType } from '@/data/store/selectors';
import { RequestParams, TApiTable } from '@/data/types';
import { useSound } from '@/hooks/useSound';

export type TResourceTypeChange = {
  table: TApiTable;
  event: 'created' | 'updated' | 'deleted';
  target: 'user' | 'organization';
  ids: string[];
  user_id?: string;
};

const ResourceActionsProvider = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const resourceType = useSelector(getResourceType);
  const PARAMS: RequestParams = { page_number: 1, page_size: 50 };
  const { playNotificationBeep, playSuccessSound } = useSound();
  const user = useSelector(getCurrentSessionUser);

  const getIdFromPath = (singleItemRoutes: string[]): string | null => {
    const currentPath = location.pathname;

    for (const route of singleItemRoutes) {
      if (currentPath.startsWith(route)) {
        // Get everything after the route, split by '/', take first part
        const afterRoute = currentPath.substring(route.length);
        const id = afterRoute.split('/')[0];
        return id || null;
      }
    }
    return null;
  };

  type ActionMapEntry = {
    routes: string[];
    dispatchAction: () => void;
    requiresParams?: boolean;
    tables: TApiTable[];
    events?: ('created' | 'updated' | 'deleted')[];
    singleItemRoutes?: string[];
    singleItemDispatchAction?: (id: string) => void;
    paramKey?: string;
  };

  const actionMap: Record<string, ActionMapEntry> = {
    // files_reload: {
    //   routes: ['/files'],
    //   dispatchAction: async () => await dispatch(getAllFilesAsync(PARAMS)),
    //   tables: ['file.file'],
    //   events: ['created', 'updated', 'deleted'],
    // },
    organizations_reload: {
      routes: ['/organization-select'],
      dispatchAction: async () => await dispatch(getAllOrganizationsAsync(PARAMS)),
      tables: ['organization.organization'],
      events: ['created', 'updated', 'deleted'],
    },
    users_reload: {
      routes: ['/personnel'],
      dispatchAction: async () => await dispatch(getAllUsersAsync(PARAMS)),
      tables: ['user.user'],
      events: ['created', 'updated', 'deleted'],
    },
  };

  const isInActionRoute = (actionEntry: ActionMapEntry): boolean => {
    const currentPath = location.pathname;
    return actionEntry.routes.some((route) => currentPath.startsWith(route));
  };

  const shouldTriggerAction = (
    actionEntry: ActionMapEntry,
    resourceChange: TResourceTypeChange,
    force: boolean = false,
  ): boolean => {
    if (force) return true;
    if (!isInActionRoute(actionEntry)) return false;
    if (!actionEntry.tables.includes(resourceChange.table)) return false;
    if (actionEntry.events && !actionEntry.events.includes(resourceChange.event)) return false;
    if (resourceChange.user_id === user?.id) return false;
    return true;
  };

  const handleSingleItemReloadsForResource = (singleResourceType: TResourceTypeChange) => {
    let soundPlayed = false;

    Object.entries(actionMap).forEach(([actionName, actionEntry]) => {
      if (!actionEntry.singleItemRoutes || !actionEntry.singleItemDispatchAction || !actionEntry.paramKey) {
        return;
      }

      if (!actionEntry.tables.includes(singleResourceType.table)) return;

      // Use the simple ID extraction
      const itemId = getIdFromPath(actionEntry.singleItemRoutes);

      console.log('Single item check:', {
        actionName,
        itemId,
        resourceTypeIds: singleResourceType.ids,
        currentPath: location.pathname,
      });

      if (itemId && singleResourceType.ids.includes(itemId)) {
        console.log(
          `Reloading single ${actionName.replace('_reload', '')} ${itemId} due to ${singleResourceType.event} event`,
        );
        actionEntry.singleItemDispatchAction(itemId);

        if (!soundPlayed) {
          playNotificationBeep();
          soundPlayed = true;
        }
      }
    });
  };

  const processResourceChange = (singleResourceType: TResourceTypeChange) => {
    let soundPlayed = false;

    Object.entries(actionMap).forEach(([actionName, actionEntry]) => {
      if (shouldTriggerAction(actionEntry, singleResourceType)) {
        if (typeof actionEntry.dispatchAction === 'function') {
          console.log(`Triggering ${actionName} due to ${singleResourceType.event} on ${singleResourceType.table}`);
          actionEntry.dispatchAction();

          if (!soundPlayed) {
            if (singleResourceType.event === 'created') {
              playSuccessSound();
            } else {
              playNotificationBeep();
            }
            soundPlayed = true;
          }
        }
      }
    });

    handleSingleItemReloadsForResource(singleResourceType);
  };

  useEffect(() => {
    if (resourceType && user) {
      // Handle both single item and array of items
      const resourceChanges = Array.isArray(resourceType) ? resourceType : [resourceType];

      resourceChanges.forEach((singleResourceType) => {
        processResourceChange(singleResourceType);
      });
    }
  }, [resourceType, user?.id]);

  return null;
};

export default ResourceActionsProvider;
